import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import WaitingListPage from "@/pages/WaitingListPage";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { Button } from "@/components/ui/button";

type ProtectedRouteProps = {
  children: ReactNode;
};

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const {
    isAuthenticated,
    isLoading,
    approvalStatus,
    approvalStatusError,
    refreshApprovalStatus,
    signOut,
  } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  if (approvalStatus === "pending") {
    return <WaitingListPage />;
  }

  // Never mount the dashboard until the API has confirmed that the current
  // database status is approved. This prevents protected data requests and UI
  // flashes when session data is stale or the status request fails.
  if (approvalStatus !== "approved") {
    return (
      <AuthLayout
        title="Unable to verify account"
        subtitle="Your dashboard access could not be checked"
        leftContent={{
          title: "Welcome to Youzu",
          subtitle: "Account access",
          description: "We need to verify your account before opening the dashboard.",
          trustIndicator: "Secure access",
        }}
      >
        <div className="space-y-6 text-center">
          <p className="text-gray-700">
            {approvalStatusError ??
              "We couldn't verify your account status. Please try again."}
          </p>
          <div className="flex justify-center gap-3">
            <Button type="button" onClick={() => void refreshApprovalStatus()}>
              Try again
            </Button>
            <Button type="button" variant="outline" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return <>{children}</>;
};
